import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { BRAND, NAV_LINKS } from "../config/brand";
import { useLang } from "../i18n/LangContext";

export const Navbar = () => {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <motion.header
      data-testid="site-navbar"
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "glass-strong py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12 flex items-center justify-between gap-6">
        <button
          data-testid="nav-logo"
          onClick={() => scrollTo("hero")}
          className="flex items-center gap-3 group"
        >
          <img
            src={BRAND.logo}
            alt="LionPerformance"
            className={`h-10 w-10 object-contain transition-all duration-500 group-hover:scale-110 ${
              scrolled ? "opacity-100 scale-100" : "opacity-0 scale-75 pointer-events-none w-0 h-0"
            }`}
            style={{ filter: "url(#lp-remove-black)" }}
          />
          <span className="font-display text-white text-lg font-semibold tracking-tight hidden sm:block">
            Lion<span className="text-primary-red">Performance</span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => scrollTo(l.id)}
              className="font-body text-sm tracking-wide text-zinc-300 hover:text-white transition-colors relative group"
            >
              {t(l.labelKey)}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-primary-red transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div
            className="flex items-center gap-1 text-xs font-semibold tracking-widest border border-white/15 rounded-full p-1"
            role="group"
            aria-label="Language switcher"
          >
            {["de", "en"].map((l) => (
              <button
                key={l}
                data-testid={`lang-switch-${l}`}
                onClick={() => setLang(l)}
                className={`px-3 py-1 rounded-full transition-all ${
                  lang === l
                    ? "bg-white text-black"
                    : "text-zinc-400 hover:text-white"
                }`}
              >
                {l.toUpperCase()}
              </button>
            ))}
          </div>

          <button
            data-testid="nav-cta-desktop"
            onClick={() => scrollTo("kontakt")}
            className="btn-primary hidden md:inline-flex items-center gap-2 px-5 py-2.5 bg-primary-red text-white text-sm font-semibold uppercase tracking-wider hover:bg-[#B3000C] transition-colors"
          >
            {t("nav.cta")}
          </button>

          <button
            data-testid="nav-menu-toggle"
            aria-label="Menu"
            className="lg:hidden text-white p-2"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden glass-strong border-t border-white/5"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-nav-link-${l.id}`}
                  onClick={() => scrollTo(l.id)}
                  className="text-left font-body text-base text-zinc-200 hover:text-primary-red transition-colors"
                >
                  {t(l.labelKey)}
                </button>
              ))}
              <button
                data-testid="mobile-nav-cta"
                onClick={() => scrollTo("kontakt")}
                className="mt-2 bg-primary-red text-white py-3 font-semibold uppercase tracking-wider text-sm"
              >
                {t("nav.cta")}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
